import { ConversationReadingPane } from 'owa-mail-reading-pane-view-conversation';
import EmptyStateReadingPane from 'owa-mail-reading-pane-view/lib/components/EmptyStateReadingPane';
import { ItemReadingPane } from 'owa-mail-reading-pane-view-item';
import NullReadingPane from './NullReadingPane';
import { observer } from 'owa-mobx-react';
import { SelectionType } from 'owa-addins-view/lib/utils/navigations/SelectionType';
import type { ClientItemId } from 'owa-client-ids';
import { lazyInitializeGetItemManagerForRP } from 'owa-get-item-manager-initialization';
import updateAddinOnItemNavigation from 'owa-mail-addins-utils/lib/updateAddinOnItemNavigation';
import findInlineComposeViewState from 'owa-mail-compose-actions/lib/utils/findInlineComposeViewState';
import { isReadingPanePositionOff } from 'owa-mail-layout/lib/selectors/readingPanePosition';
import getInstrumentationContextsFromTableView from 'owa-mail-list-store/lib/utils/getInstrumentationContextsFromTableView';
import { getMailListLoadState } from 'owa-mail-list-store/lib/utils/getMailListLoadState';
import setShouldShowMobileUpsellEmptyState from 'owa-mail-reading-pane-store/lib/actions/setShouldShowMobileUpsellEmptyState';
import readingPaneStore from 'owa-mail-reading-pane-store/lib/store/Store';
import getConversationReadingPaneViewState from 'owa-mail-reading-pane-store-conversation/lib/utils/getConversationReadingPaneViewState';
import getItemReadingPaneViewState from 'owa-mail-reading-pane-store/lib/utils/getItemReadingPaneViewState';
import { type MailListViewState } from 'owa-mail-store/lib/store/schema/MailListViewState';
import type ReactListViewType from 'owa-service/lib/contract/ReactListViewType';
import { getActiveContentTab, TabType } from 'owa-tab-store';
import type { InstrumentationContext } from 'owa-search-types/lib/types/InstrumentationContext';
import { errorThatWillCauseAlert } from 'owa-trace';
import { logUsage } from 'owa-analytics';
import React from 'react';
import {
    isMultipleSelectedItemParts,
    getSelectedTableView,
    MailRowDataPropertyGetter,
    type TableQueryType,
    getStore as getMailListStore,
    getSelectedInboxPivot,
} from 'owa-mail-list-store';
import { EmptyStateMobilePromoComponent, OutlookMobileContainer } from 'owa-guided-setup-cards';
import { shouldShowEmptyStateMobilePromo } from 'owa-emptystate-mobilepromo/lib/utils/shouldShowEmptyStateMobilePromo';
import { setPhoneAppCardDismissState } from 'owa-emptystate-mobilepromo';
import { isPopout } from 'owa-popout-v2';
import ProjectionContext from 'owa-popout-v2/lib/context/ProjectionContext';
import isAnySxSVisibleInMainWindow from 'owa-sxs-store/lib/utils/isAnySxSVisibleInMainWindow';
import { absolutePosition } from './ReadingPane.scss';
import { isComposeMailboxStateValid } from 'owa-mail-compose-actions/lib/utils/isComposeMailboxStateValid';
import type { ComposeViewState } from 'owa-mail-compose-store';
import {
    /* eslint-disable-next-line @typescript-eslint/no-restricted-imports -- (https://aka.ms/OWALintWiki)
     * Existing isFeatureEnabled usage — migrate to isGlobalFeatureEnabled or isAccountFeatureEnabled
     *	> 'isFeatureEnabled' import from 'owa-feature-flags' is restricted. Use isGlobalFeatureEnabled or isAccountFeatureEnabled from owa-feature-flags instead */
    isFeatureEnabled,
    isGlobalFeatureEnabled,
    isAccountFeatureEnabled,
} from 'owa-feature-flags';
import { getIsCurrentThemeModernImage } from 'owa-theme';
import { NotificationBarHost } from 'owa-notification-bar';
import { getModuleContextMailboxInfo } from 'owa-module-context-mailboxinfo';
import { createDropViewState, type DragData, Droppable } from 'owa-dnd';
import type DropEffect from 'owa-dnd/lib/store/schema/DropEffect';
import { lazyOnDropFile } from 'owa-mail-file-support';
import initializeRingNovaLogger from 'owa-persona-ring/lib/telemetry';
import { SelectionPopup } from 'owa-selection-popup';
import { isReadingPaneStackedView } from 'owa-mail-organization-settings/lib/utils/readingPaneStackedViewUtils';

export interface ReadingPaneProps {
    isDeeplink?: boolean;
    isDumpsterOrDumpsterSearchTable?: boolean;
    itemId?: ClientItemId;
    isFromMainWindow?: boolean;
    isItemAttachment?: boolean;
    maximizeScrollRegion?: boolean;
    popOutRowKey?: string;
    tableViewId?: string;
}

const canDropFile = (_dragInfo: DragData): DropEffect => {
    return 'copy';
};

const droppableStyle = {
    height: '100%',
};

function useReadingPaneInitAndTeardown(props: ReadingPaneProps) {
    React.useEffect(() => {
        shouldShowEmptyStateMobilePromo().then((showMobilePromo: boolean) => {
            setShouldShowMobileUpsellEmptyState(showMobilePromo);
        });

        // Intialize the get item manager for reading pane.
        lazyInitializeGetItemManagerForRP.import().then(initializeGetItemManagerForRP => {
            initializeGetItemManagerForRP();
        });

        // Initialize nova logger for Reading Pane Persona Ring.
        initializeRingNovaLogger();

        return () => {
            if (isReadingPanePositionOff()) {
                updateAddinOnItemNavigation(
                    SelectionType.NotSupported,
                    props.itemId?.mailboxInfo ?? getModuleContextMailboxInfo()
                );
            }
        };
    }, []);
}

function ReadingPane(props: ReadingPaneProps) {
    useReadingPaneInitAndTeardown(props);
    const fixRendersEnabled = isGlobalFeatureEnabled('mon-fix-renders');
    const tableView = getSelectedTableView();
    const mailListViewState = getMailListLoadState();
    const selectedRowKeys = tableView && [...tableView.selectedRowKeys?.keys()];
    const isSearchTable = tableView?.tableQuery?.type === 1;
    const selectedMessageListPivot = tableView ? getSelectedInboxPivot(tableView) : 'n/a';

    const firstSelectedRowKey = selectedRowKeys?.[0];
    const memoizedRowId = React.useMemo(
        () =>
            firstSelectedRowKey && tableView
                ? MailRowDataPropertyGetter.getRowIdToShowInReadingPane(
                      firstSelectedRowKey,
                      tableView
                  )
                : undefined,
        [firstSelectedRowKey, tableView]
    );
    const memoizedRowCategories = React.useMemo(
        () =>
            firstSelectedRowKey && tableView
                ? MailRowDataPropertyGetter.getCategories(firstSelectedRowKey, tableView)
                : [],
        [firstSelectedRowKey, tableView]
    );

    if (isSearchTable) {
        setShouldShowMobileUpsellEmptyState(false);
    }

    const {
        itemId,
        isDumpsterOrDumpsterSearchTable,
        isFromMainWindow,
        isItemAttachment,
        maximizeScrollRegion,
        popOutRowKey,
        tableViewId,
    } = props;
    const { deeplinkId, primaryReadingPaneTabId } = readingPaneStore();
    const conversationReadingPaneViewState = getConversationReadingPaneViewState();
    const activeTab = getActiveContentTab();
    const targetWindow = React.useContext(ProjectionContext);
    let readingPaneContent: JSX.Element = <NullReadingPane />;
    const composeViewState: ComposeViewState | undefined =
        conversationReadingPaneViewState?.conversationId &&
        findInlineComposeViewState(conversationReadingPaneViewState.conversationId.Id);
    const isInlineCompose = !!composeViewState?.isInlineCompose;
    const mailboxInfo =
        (deeplinkId ? deeplinkId.mailboxInfo : props.itemId?.mailboxInfo) ??
        getModuleContextMailboxInfo();
    let isConversationView = false;
    if (isInlineCompose && !isComposeMailboxStateValid(composeViewState)) {
        return readingPaneContent;
    } else if (!isFromMainWindow || !isAnySxSVisibleInMainWindow()) {
        // we render reading pane only if it is from main window with no SxS open, or, it is from a projection window
        if (deeplinkId) {
            // Deeplink UIs.
            readingPaneContent = renderItemReadingPane(
                {
                    mailboxInfo,
                    Id: deeplinkId.id,
                },
                itemId?.Id,
                isItemAttachment,
                maximizeScrollRegion,
                true /*isPopout*/,
                undefined /* subject */,
                popOutRowKey,
                selectedMessageListPivot,
                tableViewId ?? tableView?.id
            );
        } else if (itemId) {
            readingPaneContent = renderItemReadingPane(
                itemId,
                itemId?.Id,
                isItemAttachment,
                maximizeScrollRegion,
                isPopout(targetWindow),
                undefined /* subject */,
                popOutRowKey,
                selectedMessageListPivot,
                tableViewId ?? tableView?.id
            );
        } else if (activeTab && activeTab.type == TabType.SecondaryReadingPane) {
            const secondaryReadingPaneTabData = activeTab.data;
            switch (secondaryReadingPaneTabData.listViewType) {
                case 0:
                    if (!isReadingPaneStackedView(undefined /*listViewType*/)) {
                        readingPaneContent = renderItemReadingPane(
                            secondaryReadingPaneTabData.id,
                            secondaryReadingPaneTabData.id?.Id,
                            isItemAttachment,
                            maximizeScrollRegion,
                            isPopout(targetWindow),
                            secondaryReadingPaneTabData.subject,
                            popOutRowKey,
                            selectedMessageListPivot,
                            tableViewId ?? tableView?.id
                        );
                    } else {
                        if (secondaryReadingPaneTabData.id) {
                            isConversationView = true;
                            readingPaneContent = renderConversationReadingPane(
                                secondaryReadingPaneTabData.id,
                                secondaryReadingPaneTabData.subject,
                                secondaryReadingPaneTabData.categories,
                                maximizeScrollRegion,
                                undefined /* instrumentationContext */,
                                popOutRowKey,
                                selectedMessageListPivot,
                                tableViewId ?? tableView?.id
                            );
                        } else {
                            errorThatWillCauseAlert(
                                'ReadingPane.render() secondaryReadingPaneTabData.id should not be null.'
                            );
                        }
                    }
                    break;
                case 1:
                    readingPaneContent = renderItemReadingPane(
                        secondaryReadingPaneTabData.id,
                        undefined /* idToUseForViewState */,
                        isItemAttachment,
                        maximizeScrollRegion,
                        isPopout(targetWindow),
                        secondaryReadingPaneTabData.subject,
                        popOutRowKey,
                        selectedMessageListPivot,
                        tableViewId ?? tableView?.id
                    );
                    break;
            }
        } else if (composeViewState) {
            // If RP has inline compose open, render conversation in RP store.
            isConversationView = true;
            readingPaneContent = renderConversationReadingPane(
                conversationReadingPaneViewState.conversationId,
                conversationReadingPaneViewState.conversationSubject,
                conversationReadingPaneViewState.conversationCategories,
                maximizeScrollRegion,
                undefined /* instrumentationContext */,
                popOutRowKey,
                selectedMessageListPivot,
                tableViewId ?? tableView?.id
            );
        } else if (
            selectedRowKeys?.length == 1 &&
            !isMultipleSelectedItemParts() &&
            !tableView?.isInVirtualSelectAllMode &&
            !isDumpsterOrDumpsterSearchTable &&
            getMailListStore().shouldShowSelectedRowInRp
        ) {
            // 1 row selected: display reading pane only if select all is not checked and not in dumpster or dumpster search
            const rowId = MailRowDataPropertyGetter.getRowIdToShowInReadingPane(
                selectedRowKeys[0],
                tableView
            );
            const rowSubject = MailRowDataPropertyGetter.getSubject(selectedRowKeys[0], tableView);
            const rowCategories = MailRowDataPropertyGetter.getCategories(
                selectedRowKeys[0],
                tableView
            );
            switch (tableView?.tableQuery.listViewType) {
                case 0:
                    if (
                        !isReadingPaneStackedView(tableView?.tableQuery.listViewType) &&
                        !!primaryReadingPaneTabId
                    ) {
                        readingPaneContent = renderItemReadingPane(
                            primaryReadingPaneTabId,
                            undefined /* idToUseForViewState */,
                            isItemAttachment,
                            maximizeScrollRegion,
                            isPopout(targetWindow),
                            rowSubject,
                            undefined /* popOutRowKey */,
                            selectedMessageListPivot,
                            tableViewId ?? tableView?.id
                        );
                    } else {
                        if (rowId && memoizedRowId) {
                            const instrumentationContext = getInstrumentationContextsFromTableView(
                                [selectedRowKeys[0]],
                                tableView
                            )[0];
                            isConversationView = true;
                            readingPaneContent = renderConversationReadingPane(
                                fixRendersEnabled ? memoizedRowId : rowId,
                                rowSubject,
                                fixRendersEnabled ? memoizedRowCategories : rowCategories,
                                maximizeScrollRegion,
                                instrumentationContext,
                                popOutRowKey,
                                selectedMessageListPivot,
                                tableViewId ?? tableView?.id
                            );
                        } else {
                            logUsage('RPConversationRowIdNullError', mailboxInfo);
                        }
                    }
                    break;
                case 1:
                    if (rowId && memoizedRowId) {
                        readingPaneContent = renderItemReadingPane(
                            fixRendersEnabled ? memoizedRowId : rowId,
                            fixRendersEnabled ? memoizedRowId?.Id : rowId?.Id,
                            isItemAttachment,
                            maximizeScrollRegion,
                            isPopout(targetWindow),
                            rowSubject,
                            undefined /* popOutRowKey */,
                            selectedMessageListPivot,
                            tableViewId ?? tableView?.id
                        );
                    }
                    break;
            }
        } else if (
            mailListViewState === 'Loaded' ||
            mailListViewState === 'Empty' ||
            readingPaneStore().shouldShowEmptyReadingPane
        ) {
            // Empty state.
            const themeHasBackgroundImage = getIsCurrentThemeModernImage();
            const shouldRenderMobileUpsell =
                readingPaneStore().shouldShowMobileUpsellEmptyState && !themeHasBackgroundImage;

            readingPaneContent = shouldRenderMobileUpsell ? (
                <EmptyStateMobilePromoComponent
                    containerName={OutlookMobileContainer.ReadingPane}
                    onComponentUnmount={onOutlookMobileUnmount}
                />
            ) : (
                <EmptyStateReadingPane
                    isDumpsterOrDumpsterSearchTable={isDumpsterOrDumpsterSearchTable}
                    mailListViewState={mailListViewState}
                />
            );
        }
    }

    function onOutlookMobileUnmount() {
        setPhoneAppCardDismissState(true);
        setShouldShowMobileUpsellEmptyState(false);
    }

    const paneInner = (
        <>
            {readingPaneContent}
            {/* eslint-disable-next-line owa-custom-rules/require-undefined-parameter -- (https://aka.ms/OWALintWiki)
             * Flight checks that supply MailboxInfo should be defined as AccountFeatureName value and should be checked using isAccountFeatureEnabled to ensure consistent checking.
             *	> The parameter mailboxInfo must be undefined. Feature flight: 'doc-mon-openClassicInNative' */}
            {(isFeatureEnabled(
                'doc-mon-openClassicInNative',
                props.itemId?.mailboxInfo,
                true /* dontThrowErrorIfNotInitialized */
            ) ||
                isAccountFeatureEnabled('cmp-copilot-bizchat', mailboxInfo)) && (
                <NotificationBarHost
                    hostId={'ItemNotificationBarHost'}
                    targetWindow={targetWindow}
                />
            )}
            <SelectionPopup isConversationView={isConversationView} targetWindow={targetWindow} />
        </>
    );

    return (
        <div key="readingPaneDiv" className={absolutePosition}>
            {isGlobalFeatureEnabled('mon-file-eml-msg') && !isInlineCompose ? (
                <Droppable
                    dropViewState={createDropViewState()}
                    onDrop={lazyOnDropFile.importAndExecute}
                    canDrop={canDropFile}
                    shouldIgnoreTransientOnDragLeave={true}
                    style={droppableStyle}
                >
                    {paneInner}
                </Droppable>
            ) : (
                paneInner
            )}
        </div>
    );
}

function renderItemReadingPane(
    itemId: ClientItemId,
    idUsedForViewState: string | undefined,
    isItemAttachment?: boolean,
    maximizeScrollRegion?: boolean,
    openInPopout?: boolean,
    subject?: string,
    popOutRowKey?: string,
    selectedMessageListPivot?: string,
    tableViewId?: string
): JSX.Element {
    const itemReadingPaneViewState = getItemReadingPaneViewState(idUsedForViewState);
    return (
        <ItemReadingPane
            itemId={itemId}
            itemSubject={subject}
            itemReadingPaneViewState={itemReadingPaneViewState}
            isPopout={openInPopout}
            isItemAttachment={isItemAttachment}
            maximizeScrollRegion={maximizeScrollRegion}
            popOutRowKey={popOutRowKey}
            selectedMessageListPivot={selectedMessageListPivot}
            tableViewId={tableViewId}
        />
    );
}

function renderConversationReadingPane(
    conversationId: ClientItemId,
    subject: string,
    categories: string[],
    maximizeScrollRegion?: boolean,
    instrumentationContext?: InstrumentationContext,
    popOutRowKey?: string,
    selectedMessageListPivot?: string,
    tableViewId?: string
): JSX.Element {
    return (
        <ConversationReadingPane
            conversationId={conversationId}
            conversationSubject={subject}
            conversationCategories={categories}
            instrumentationContext={instrumentationContext}
            maximizeScrollRegion={maximizeScrollRegion}
            popOutRowKey={popOutRowKey}
            selectedMessageListPivot={selectedMessageListPivot ?? 'n/a'}
            tableViewId={tableViewId}
        />
    );
}

const defaultExport: React.FunctionComponent<ReadingPaneProps> = observer(
    ReadingPane,
    'ReadingPane'
);
export default defaultExport;
